import { createI18n } from 'petite-vue-i18n'
import { computed, shallowRef } from 'vue'

import type de from '@/translations/de.json'
import type { UILanguage } from '@/types'
import logging from '@/utils/logging'

export type MessageSchema = typeof de
export type TranslationKey = keyof MessageSchema | ''

const localeLoaders = {
  de: () => import('@/translations/de.json'),
  it: () => import('@/translations/it.json'),
  en: () => import('@/translations/en.json')
}
const localeLoads = new Map<UILanguage, Promise<void>>()
const loadedLocales = new Set<UILanguage>()

export function normalizeLocale(language: string | null | undefined): UILanguage {
  const languageCode = language?.split('-')[0]
  return languageCode === 'de' || languageCode === 'it' || languageCode === 'en' ? languageCode : 'de'
}


const fallbackLocale = normalizeLocale(import.meta.env.VITE_APP_I18N_FALLBACK_LOCALE)
const requestedLocale = shallowRef<UILanguage>(normalizeLocale(
  localStorage.getItem('user_language') || navigator.language || import.meta.env.VITE_APP_I18N_LOCALE
))

const i18n = createI18n<[MessageSchema], UILanguage, false>({
  legacy: false,
  globalInjection: false,
  locale: requestedLocale.value,
  fallbackLocale: fallbackLocale,
  // Leere Platzhalter erhalten die Schema-Typisierung, ohne JSON-Dateien zu laden.
  messages: { de: {} as MessageSchema, it: {} as MessageSchema, en: {} as MessageSchema },
  missing: (language) => {
    // Die Fallback-Locale erst bei einer tatsächlich fehlenden Übersetzung laden.
    if (loadedLocales.has(language as UILanguage) && !loadedLocales.has(fallbackLocale)) {
      loadLocaleMessages(fallbackLocale).catch(logging.error)
    }
  }
})


/** Lädt jede Sprache einmal; parallele Aufrufe teilen denselben Ladevorgang. */
export function loadLocaleMessages(language: UILanguage): Promise<void> {
  const existingLoad = localeLoads.get(language)
  if (existingLoad) return existingLoad

  const load = localeLoaders[language]().then(({ default: translations }) => {
    const messages = { ...translations }
    for (const key of Object.keys(messages) as (keyof MessageSchema)[]) {
      if (!messages[key]) {
        logging.warn(`Fehlende Übersetzung für "${key}" in Locale ${language}`)
        delete messages[key]
      }
    }
    i18n.global.setLocaleMessage(language, messages)
    loadedLocales.add(language)
  }).catch((error: unknown) => {
    localeLoads.delete(language)
    throw error
  })
  localeLoads.set(language, load)
  return load
}

/** Aktiviert nur die zuletzt angeforderte Sprache und erst nach dem Laden. */
export async function setLocale(language: string): Promise<void> {
  const normalizedLocale = normalizeLocale(language)
  requestedLocale.value = normalizedLocale
  try {
    if (!loadedLocales.has(normalizedLocale)) await loadLocaleMessages(normalizedLocale)
  } catch (error) {
    if (requestedLocale.value === normalizedLocale) {
      requestedLocale.value = i18n.global.locale.value
    }
    throw error
  }
  if (requestedLocale.value === normalizedLocale) {
    i18n.global.locale.value = normalizedLocale
    document.documentElement.lang = normalizedLocale
  }
}

// Bestehende locale.value-Zuweisungen bleiben synchron lesbar; übersetzt wird
// während des Nachladens weiterhin mit der zuletzt vollständig geladenen Sprache.
export const locale = computed<UILanguage>({
  get: () => requestedLocale.value,
  set: (language) => { void setLocale(language).catch(logging.error) }
})

export default i18n
